<template>
  <div
    v-if="isOpen"
    id="detail-modal"
    class="modal-overlay"
    style="display: flex"
    @click.self="closeDetail"
  >
    <div class="modal" v-if="currentSection">
      <div class="modal-header">
        <div class="modal-title">{{ currentSection.emoji }} {{ currentSection.title }} — Detail</div>
        <button class="modal-close" @click="closeDetail">✕</button>
      </div>
      <div class="modal-body">
        <!-- Stats -->
        <div class="detail-stats">
          <div class="detail-stat">
            <div class="detail-stat-value" style="color: var(--accent-green)">
              {{ currentSection.detail.peak.value }}
            </div>
            <div class="detail-stat-label">🔺 Peak ({{ currentSection.detail.peak.day }})</div>
          </div>
          <div class="detail-stat">
            <div class="detail-stat-value" style="color: var(--accent-red)">
              {{ currentSection.detail.low.value }}
            </div>
            <div class="detail-stat-label">🔻 Low ({{ currentSection.detail.low.day }})</div>
          </div>
          <div class="detail-stat">
            <div class="detail-stat-value" style="color: var(--accent-blue)">
              {{ currentSection.detail.weekdayAvg }}
            </div>
            <div class="detail-stat-label">📅 Weekday Avg</div>
          </div>
        </div>

        <!-- Summary -->
        <div class="modal-section">
          <div class="modal-section-title">Summary</div>
          <p style="font-size: 12px; line-height: 1.7; color: var(--text-body); background: var(--bg-card); padding: 12px; border-radius: 8px; border: 1px solid var(--border)">
            {{ currentSection.detail.summary }}
          </p>
        </div>

        <!-- Table -->
        <div class="modal-section">
          <div class="modal-section-title">Daily Breakdown</div>
          <div class="table-wrap">
            <table class="detail-table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Actual</th>
                <th>Avg</th>
                <th>Status</th>
                <th>Δ%</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(d, i) in currentSection.data" :key="i">
                <td style="font-weight: 600; color: var(--text-body)">{{ d.day }}</td>
                <td>
                  <div style="display: flex; align-items: center; gap: 8px">
                    <span style="font-weight: 700; color: var(--text-primary)">{{ d.val }}</span>
                    <div class="mini-bar" style="flex: 1; max-width: 60px">
                      <div
                        class="mini-bar-fill"
                        :style="{
                          width: (d.val / maxVal) * 100 + '%',
                          background: d.val >= d.avg ? 'var(--accent-green)' : 'var(--accent-red)',
                        }"
                      ></div>
                    </div>
                  </div>
                </td>
                <td style="color: var(--text-faint)">{{ d.avg }}</td>
                <td>
                  <span :class="getBadgeClass(d)">
                    {{ d.val > d.avg ? '▲' : d.val < d.avg ? '▼' : '→' }}
                  </span>
                </td>
                <td>
                  <span :class="getBadgeClass(d)">
                    {{ getDeltaPercent(d) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>

        <!-- Insight -->
        <div class="modal-section">
          <div class="modal-section-title">💡 AI Insight</div>
          <div style="font-size: 12px; line-height: 1.7; color: var(--text-body); background: rgba(139, 92, 246, 0.06); border: 1px solid rgba(139, 92, 246, 0.15); padding: 14px; border-radius: 10px">
            {{ currentSection.detail.insight }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useDetailModal } from '../composables/useDetailModal'

const { isOpen, currentSection, closeDetail } = useDetailModal()

const maxVal = computed(() => {
  if (!currentSection.value) return 1
  return Math.max(...currentSection.value.data.map((d) => d.val), 1)
})

function getBadgeClass(d) {
  if (d.val > d.avg) return 'badge-up'
  if (d.val < d.avg) return 'badge-down'
  return 'badge-flat'
}

function getDeltaPercent(d) {
  const p = d.avg > 0 ? ((d.val - d.avg) / d.avg * 100).toFixed(0) : 0
  return (p > 0 ? '+' : '') + p + '%'
}
</script>
